import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { inputClass } from "@/components/base";
import { api } from "@/lib/api";

export const communeLabel = (c) => c.label || `${c.nom} (${c.departement?.code || c.departement || ""})`;
const norm = (s) =>
  (s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/-/g, " ")
    .trim();
const newSession = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const LOAD_ERR = "Impossible de charger les villes. Réessaie dans un instant.";

async function fetchCities(q, session, signal) {
  const r = await api.get("/cities", { params: { q, session }, signal });
  return r.data.items || [];
}

export default function CityInput({ value, onChange, error, inputRef, resolverRef, testId = "city-input", bare = false, onLoadError }) {
  const [text, setText] = useState(value ? communeLabel(value) : "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const boxRef = useRef(null);
  const session = useRef(newSession());
  const lastQ = useRef("");

  useEffect(() => {
    const onDoc = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    const q = text.trim();
    if (value || q.length < 2) {
      setItems([]);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const data = await fetchCities(q, session.current, ctrl.signal);
        lastQ.current = q;
        setItems(data);
        setActive(0);
        setOpen(true);
        setLoadError(false);
      } catch (e) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          setLoadError(true);
          setItems([]);
        }
      }
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [text, value]);

  const toCommune = (c) => ({
    code: c.code,
    nom: c.nom,
    departement: c.departement,
    country: c.country,
    label: c.label,
    session: c.country !== "FR" ? session.current : null,
  });

  const pick = (c) => {
    const picked = toCommune(c);
    onChange(picked);
    setText(communeLabel(c));
    setOpen(false);
    session.current = newSession();
    return picked;
  };

  useImperativeHandle(resolverRef, () => ({
    // Auto-select on submit: a single suggestion, or a single exact name match
    resolve: async () => {
      const q = text.trim();
      if (q.length < 2) return null;
      let list = items;
      if (lastQ.current !== q) {
        try {
          list = await fetchCities(q, session.current);
          lastQ.current = q;
        } catch {
          return null;
        }
      }
      const exact = list.filter((c) => norm(c.nom) === norm(q) || norm(c.label) === norm(q));
      const exactFr = exact.filter((c) => (c.country || "FR") === "FR");  // « Lens » = Lens (62), not Lens BE / CH
      const c = list.length === 1 ? list[0] : exact.length === 1 ? exact[0] : exactFr.length === 1 ? exactFr[0] : null;
      if (c) return pick(c);
      setItems(list);
      setActive(0);
      setOpen(list.length > 0);
      return null;
    },
  }));

  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(items[active]);
    } else if (e.key === "Escape") setOpen(false);
  };

  const message = loadError ? LOAD_ERR : error;

  useEffect(() => {
    onLoadError?.(loadError ? LOAD_ERR : "");
  }, [loadError, onLoadError]);

  const inputCls = bare
    ? "h-full w-full min-w-0 bg-transparent px-2 text-[16px] text-ink placeholder:text-muted outline-none focus-visible:outline-none"
    : `${inputClass} ${message ? "border-[color:var(--warning)]" : ""}`;

  return (
    <div ref={boxRef} className={`${bare ? "h-full" : "relative"} min-w-0 flex-1`}>
      <input
        ref={inputRef}
        data-testid={testId}
        className={inputCls}
        placeholder="Ta ville"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-label="Ta ville"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (value) onChange(null);
        }}
        onFocus={() => items.length && setOpen(true)}
        onKeyDown={onKey}
      />
      {open && items.length > 0 && (
        <ul
          role="listbox"
          data-testid="city-options"
          className="fade-up absolute left-0 right-0 top-[calc(100%+8px)] z-30 overflow-hidden rounded-card border border-line bg-surface py-1 shadow-modal"
        >
          {items.map((c, i) => (
            <li key={c.code} role="option" aria-selected={i === active}>
              <button
                type="button"
                data-testid={`city-option-${c.code.startsWith("g:") ? c.nom.toLowerCase().replace(/\s+/g, "-") : c.code}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(c)}
                className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-[15px] ${i === active ? "bg-surface-2" : ""}`}
              >
                <span className="truncate text-ink">{communeLabel(c)}</span>
                {c.postal && <span className="num shrink-0 text-xs text-muted">{c.postal}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {!bare && message && (
        <p data-testid="city-error" className="mt-1.5 text-[13px] err-msg text-ink">
          {message}
        </p>
      )}
    </div>
  );
}
